/**
 * DelegatedWorkInbox.tsx — Inbox view for delegations.
 * 
 * Tabs:
 * 1. Delegated to me (Active)
 * 2. Delegated by me (Active)
 * 3. Pending updates
 * 4. Completed/Recalled/Cancelled
 * 
 * Tab counts are fetched for badge display.
 * Auth infers current user from token.
 */

import React, { useEffect, useState } from 'react';
import { DelegationSummary, PendingUpdateSummary, InboxTabCounts } from '../types/delegation';
import {
  getDelegatedToMe,
  getDelegatedByMe,
  getPendingUpdates,
  getAwaitingResponse,
  getCompletedDelegations,
} from '../api/delegationsApi';
import DelegationList from '../components/delegations/DelegationList';
import './DelegatedWorkInbox.scss';

type TabName = 'delegated-to-me' | 'delegated-by-me' | 'pending-updates' | 'awaiting-response' | 'completed';

export const DelegatedWorkInbox: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabName>('delegated-to-me');
  const [counts, setCounts] = useState<InboxTabCounts>({
    delegatedToMe: 0,
    delegatedByMe: 0,
    pendingUpdates: 0,
    awaitingResponse: 0,
    completed: 0,
  });

  // Tab-specific states
  const [delegatedToMe, setDelegatedToMe] = useState<DelegationSummary[]>([]);
  const [delegatedByMe, setDelegatedByMe] = useState<DelegationSummary[]>([]);
  const [pendingUpdates, setPendingUpdates] = useState<PendingUpdateSummary[]>([]);
  const [awaitingResponse, setAwaitingResponse] = useState<PendingUpdateSummary[]>([]);
  const [completed, setCompleted] = useState<DelegationSummary[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all data on mount
  useEffect(() => {
    const fetchAllTabs = async () => {
      try {
        setLoading(true);
        setError(null);

        const [delegToMe, delegByMe, pending, awaiting, comp] = await Promise.all([
          getDelegatedToMe('Active'),
          getDelegatedByMe('Active'),
          getPendingUpdates(),
          getAwaitingResponse(),
          getCompletedDelegations(),
        ]);

        setDelegatedToMe(delegToMe);
        setDelegatedByMe(delegByMe);
        setPendingUpdates(pending);
        setAwaitingResponse(awaiting);
        setCompleted(comp);

        setCounts({
          delegatedToMe: delegToMe.length,
          delegatedByMe: delegByMe.length,
          pendingUpdates: pending.length,
          awaitingResponse: awaiting.length,
          completed: comp.length,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load delegations';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllTabs();
  }, []);

  const tabs: Array<{ id: TabName; label: string; count: number }> = [
    { id: 'delegated-to-me', label: 'Delegated to me', count: counts.delegatedToMe },
    { id: 'delegated-by-me', label: 'Delegated by me', count: counts.delegatedByMe },
    { id: 'pending-updates', label: 'Pending updates', count: counts.pendingUpdates },
    { id: 'awaiting-response', label: 'Awaiting response', count: counts.awaitingResponse },
    { id: 'completed', label: 'Completed/Recalled', count: counts.completed },
  ];

  return (
    <div className="delegated-work-inbox">
      <div className="inbox-header">
        <h1>Delegated Work</h1>
        <p className="subtitle">Manage your delegations and update requests</p>
      </div>

      {error && (
        <div className="inbox-error">
          <p>{error}</p>
        </div>
      )}

      <div className="inbox-tabs">
        <div className="tabs-header">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-label">{tab.label}</span>
              <span className="tab-count">{tab.count}</span>
            </button>
          ))}
        </div>

        <div className="tabs-content">
          {/* Delegated to me */}
          {activeTab === 'delegated-to-me' && (
            <div className="tab-pane">
              <DelegationList
                items={delegatedToMe}
                loading={loading}
                error={error}
                emptyMessage="No delegations assigned to you"
              />
            </div>
          )}

          {/* Delegated by me */}
          {activeTab === 'delegated-by-me' && (
            <div className="tab-pane">
              <DelegationList
                items={delegatedByMe}
                loading={loading}
                error={error}
                emptyMessage="You haven't delegated any work yet"
                showDelegator={true}
              />
            </div>
          )}

          {/* Pending updates */}
          {activeTab === 'pending-updates' && (
            <div className="tab-pane">
              <DelegationList
                items={pendingUpdates}
                loading={loading}
                error={error}
                emptyMessage="No pending update requests"
                showDelegator={true}
              />
            </div>
          )}

          {/* Awaiting response */}
          {activeTab === 'awaiting-response' && (
            <div className="tab-pane">
              <DelegationList
                items={awaitingResponse}
                loading={loading}
                error={error}
                emptyMessage="No outstanding update requests"
              />
            </div>
          )}

          {/* Completed/Recalled */}
          {activeTab === 'completed' && (
            <div className="tab-pane">
              <DelegationList
                items={completed}
                loading={loading}
                error={error}
                emptyMessage="No completed or recalled delegations"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DelegatedWorkInbox;
